import { IPageContent } from "api/interfaces/static-content.type";
import PAText from "component/design-system/typography/PAText/PAText";
import VizSensor from "component/design-system/VizSensor";
import ProductReviews from "component/product/V2/reviews/ProductReviews";
import useHaveScoll from "hoc/useHaveScoll";
import useScreenSize from "hoc/useScreenSize";
import { RootState } from "interfaces/generics.type";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { getProductCategory } from "util/product/get-product-category";
import { validateVehicle } from "util/vehicle/validate-vehicle";
import styles from "./product.module.scss";

const ProductLinesSlider = dynamic(() => import(/* webpackChunkName: "product-manufature-lines" */"./ProductLinesSlider"));
const BrandsInSameCategory = dynamic(() => import(/* webpackChunkName: "product-brands-in-same-category" */"./brands-in-same-category/BrandsInSameCategory"));
const MakeModelTierLinks = dynamic(() => import(/* webpackChunkName: "product-make-model-tier-links" */"modules/product/MakeModelTierLinks"));
const FrequentlyBoughtTogether = dynamic(() => import(/* webpackChunkName: "product-fbt" */"./fbt/FrequentlyBoughtTogether"));

const SimilarProduct = dynamic(() => import(/* webpackChunkName: "product-similar-product" */"component/product/similar-products/SimilarProduct"), {
  ssr: false,
});
const ShowSimilarProduct = dynamic(() => import(/* webpackChunkName: "similar-products" */ "component/product/similar-products/ShowSimilarProduct"));
const MerchandiseSlider = dynamic(() => import(/* webpackChunkName: "merchandise-slider" */ "modules/home/merchandise-slider/MerchandiseSlider"));
const ManufacturerVideo = dynamic(() => import(/* webpackChunkName: "manufacturer-video" */ "modules/product/ManufacturerVideo"));
const ManufacturerDescription = dynamic(() => import(/* webpackChunkName: "manufacturer-description" */ "modules/product/ManufacturerDescription"));
const ProductRecommendations = dynamic(() => import(/* webpackChunkName: "product-recommendations" */ "modules/product/recommendations/ProductRecommendations"), {
  ssr: false,
});

interface IProps{
  staticContent: IPageContent;
}

function ProductCommon(props:IProps) {
  const {
    staticContent, productRecentlyViewedSection, product, sideBarHandler, showMerchandiseSlider,
  } = props;
  const { t } = useTranslation("product");
  const selectedVehicle = useSelector((state: RootState) => state.garage.selectedVehicle);
  const [haveScroled] = useHaveScoll();
  const { tier2MakeModels, tier3MakeModels, productLines } = staticContent.links || {};
  const [isMobile] = useScreenSize();

  const exploreMoreLines = productLines?.filter((el) => el.linkDetails?.title);
  return (
    <div className={styles.product_common_container}>
      <VizSensor>
        <HideIf show={validateVehicle(selectedVehicle)}>
          <FrequentlyBoughtTogether
            product={product}
          />
        </HideIf>
      </VizSensor>

      <ProductReviews product={product} />

      {/* https://partsavatar.ca/raybestos-front-disc-brake-rotor-680180r */}
      <HideIf show={staticContent.pageStructure && staticContent.pageStructure.length}>
        <HideIf show={staticContent.manufacturerVideos?.length > 0}>
          <ManufacturerVideo manufacturerVideos={staticContent.manufacturerVideos} />
        </HideIf>
        <VizSensor>
          <ManufacturerDescription items={staticContent.pageStructure} images={staticContent.bannerImagesV2} />
        </VizSensor>
      </HideIf>

      {/* https://partsavatar.ca/raybestos-front-disc-brake-rotor-680180r */}
      <VizSensor>
        <HideIf show={!(!exploreMoreLines || exploreMoreLines.length === 0)}>
          <ProductLinesSlider
            productLines={productLines}
            manufacturer={product.manufacturer}
          />
        </HideIf>
      </VizSensor>

      {/* https://partsavatar.ca/raybestos-front-disc-brake-rotor-680180r */}
      <VizSensor>
        <div className="hidden-print">
          <SimilarProduct
            bottom={-1500}
            ignoreSku={product.sku}
            description={product.description}
            category={getProductCategory(product)}
            sideBarHandler={sideBarHandler}
            components={{
              ShowSimilarProduct,
            }}
            showViewAll={!isMobile}
          />
        </div>
      </VizSensor>

      <HideIf show={haveScroled}>
        <BrandsInSameCategory
          sku={product.sku}
        />
      </HideIf>

      {productRecentlyViewedSection}

      {/* https://partsavatar.ca/raybestos-front-disc-brake-rotor-680180r */}
      <HideIf show={(tier2MakeModels || tier3MakeModels) && !validateVehicle(selectedVehicle)}>
        <MakeModelTierLinks
          tier2MakeModels={tier2MakeModels}
          tier3MakeModels={tier3MakeModels}
        />
      </HideIf>

      <HideIf show={showMerchandiseSlider}>
        <VizSensor>
          <>
            <PAText
              component="h2"
              className="product_common_title"
              inlineBlock
              bold
            >
              {t("Shop Merchandise")}
            </PAText>
            <MerchandiseSlider />
          </>
        </VizSensor>
      </HideIf>

      <ProductRecommendations product={product} />
    </div>
  );
}

export default ProductCommon;
